
import { useEffect, ReactNode } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import StepContainer from "../components/StepContainer";
import CreativeUploadCard from "../components/content/CreativeUploadCard";
import ContentStepLayout from "../components/content/ContentStepLayout";
import { useContentStep } from "../hooks/useContentStep";
import { useCustomMaterialLocations } from "../hooks/useCustomMaterialLocations";
import { useContentStepValidation } from "../components/content/ContentStepValidation";
import { saveCreativesToStorage, saveCampaignName, collectAllCreativeUrls } from "../utils/contentStepStorage";
import { Creative, Location } from "../types";
import { useStepValidation } from "../hooks/useStepValidation";

interface ContentStepProps {
  creatives: Creative[];
  locationCreatives: Record<string, Creative[]>;
  mediaOwnerCreatives: Record<string, Creative[]>;
  selectedLocations: Location[];
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCreative: (index: number) => void;
  onLocationFileChange: (locationName: string, e: React.ChangeEvent<HTMLInputElement>, day?: number) => void;
  onRemoveLocationCreative: (locationName: string, index: number) => void;
  onUpdateLocationCreativeDays?: (locationName: string, creativeIndex: number, days: number[]) => void;
  onMediaOwnerFileChange: (mediaOwnerName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMediaOwnerCreative: (mediaOwnerName: string, index: number) => void;
  onUpdateMediaOwnerCreativeDays?: (mediaOwnerName: string, creativeIndex: number, days: number[]) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onAreaClick: () => void;
  isUploading: boolean;
  uploadProgress?: number;
  onNext: () => void;
  onBack: () => void;
  useLocationSpecificCreatives: boolean;
  onToggleLocationSpecificCreatives: (value: boolean) => void;
  useMediaOwnerSpecificCreatives: boolean;
  onToggleMediaOwnerSpecificCreatives: (value: boolean) => void;
  campaignName: string;
  setCampaignName: (name: string) => void;
  aiAssistantButton?: ReactNode;
  onUrlSubmit?: (url: string) => void;
  onLocationUrlSubmit?: (locationName: string, url: string) => void;
  onMediaOwnerUrlSubmit?: (mediaOwnerName: string, url: string, orientation?: "portrait" | "landscape") => void;
}

const ContentStep = ({
  creatives,
  locationCreatives,
  mediaOwnerCreatives,
  selectedLocations,
  onFileChange,
  onRemoveCreative,
  onLocationFileChange,
  onRemoveLocationCreative,
  onUpdateLocationCreativeDays,
  onMediaOwnerFileChange,
  onRemoveMediaOwnerCreative,
  onUpdateMediaOwnerCreativeDays,
  fileInputRef,
  onAreaClick,
  isUploading,
  uploadProgress,
  onNext,
  onBack,
  useLocationSpecificCreatives,
  onToggleLocationSpecificCreatives,
  useMediaOwnerSpecificCreatives,
  onToggleMediaOwnerSpecificCreatives,
  campaignName,
  setCampaignName,
  aiAssistantButton,
  onUrlSubmit,
  onLocationUrlSubmit,
  onMediaOwnerUrlSubmit
}: ContentStepProps) => {
  const { t } = useLanguage();
  const { isCreativesStepValid } = useStepValidation();
  
  // Real-time validation (disabled for dev)
  const isNextDisabled = false; // Allow skipping for dev
  
  const { uniqueMediaOwners } = useContentStep({
    creatives,
    locationCreatives,
    mediaOwnerCreatives,
    selectedLocations,
    useLocationSpecificCreatives,
    useMediaOwnerSpecificCreatives,
    onToggleMediaOwnerSpecificCreatives,
    campaignName
  });

  const { customMaterialLocations } = useCustomMaterialLocations(selectedLocations);

  const { validateStep } = useContentStepValidation({
    campaignName,
    customMaterialLocations,
    locationCreatives
  });

  // Save creatives URLs to localStorage when they change
  useEffect(() => {
    saveCreativesToStorage(
      creatives,
      locationCreatives,
      mediaOwnerCreatives,
      useMediaOwnerSpecificCreatives
    );
  }, [creatives, mediaOwnerCreatives, locationCreatives, useMediaOwnerSpecificCreatives]);

  // Handle step completion
  const handleNextStep = () => {
    // Skip validation for dev
    // if (!validateStep()) {
    //   return;
    // }
    
    // Save campaign name (use default if empty for dev)
    const finalCampaignName = campaignName.trim() || "Dev Campaign";
    saveCampaignName(finalCampaignName);
    
    // Collect all creative URLs
    const allCreativeUrls = collectAllCreativeUrls(
      creatives,
      mediaOwnerCreatives,
      locationCreatives,
      useMediaOwnerSpecificCreatives,
      useLocationSpecificCreatives
    );
    
    // Save all compiled creative URLs
    localStorage.setItem('allCreativeUrls', JSON.stringify(allCreativeUrls));
    
    // Proceed to next step
    onNext();
  };

  return (
    <StepContainer
      title={t("content")}
      description={t("uploadCreativesDesc")}
      onNext={handleNextStep}
      onBack={onBack}
      isNextDisabled={isNextDisabled}
      aiAssistantButton={aiAssistantButton}
    >
      <ContentStepLayout
        customMaterialLocations={customMaterialLocations}
        locationCreatives={locationCreatives}
        mediaOwnerCreatives={mediaOwnerCreatives}
        onLocationFileChange={onLocationFileChange}
        onRemoveLocationCreative={onRemoveLocationCreative}
        onUpdateLocationCreativeDays={onUpdateLocationCreativeDays}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        selectedLocations={selectedLocations}
      >
        <CreativeUploadCard 
          creatives={creatives}
          locationCreatives={locationCreatives}
          mediaOwnerCreatives={mediaOwnerCreatives}
          selectedLocations={selectedLocations}
          uniqueMediaOwners={uniqueMediaOwners}
          onFileChange={onFileChange}
          onRemoveCreative={onRemoveCreative}
          onLocationFileChange={onLocationFileChange}
          onRemoveLocationCreative={onRemoveLocationCreative}
          onUpdateLocationCreativeDays={onUpdateLocationCreativeDays}
          onMediaOwnerFileChange={onMediaOwnerFileChange}
          onRemoveMediaOwnerCreative={onRemoveMediaOwnerCreative}
          onUpdateMediaOwnerCreativeDays={onUpdateMediaOwnerCreativeDays}
          fileInputRef={fileInputRef}
          onAreaClick={onAreaClick}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          useLocationSpecificCreatives={useLocationSpecificCreatives}
          onToggleLocationSpecificCreatives={onToggleLocationSpecificCreatives}
          useMediaOwnerSpecificCreatives={useMediaOwnerSpecificCreatives}
          onToggleMediaOwnerSpecificCreatives={onToggleMediaOwnerSpecificCreatives}
          onUrlSubmit={onUrlSubmit}
          onLocationUrlSubmit={onLocationUrlSubmit}
          onMediaOwnerUrlSubmit={onMediaOwnerUrlSubmit}
        />
      </ContentStepLayout>
    </StepContainer>
  );
};

export default ContentStep;
